import { Component, DestroyRef, OnInit, computed, forwardRef, inject, input } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SfoUiJSONSchema7 } from '../../../../metadata.model';
import { MatTooltip } from '@angular/material/tooltip';

const UNITS = [
  'pt',
  'px',
  'pc',
  'mm',
  'cm',
  'in',
  'q',
  'rem',
  'em',
  'ex',
  'ch',
  'vw',
  'vh',
  'vmin',
  'vmax',
  '%',
] as const;
const UNIT_PATTERN = UNITS.map((unit) => unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
const LENGTH_REGEX = new RegExp(`^([0-9]*\\.?[0-9]+)(${UNIT_PATTERN})$`);
const KEYWORDS = ['inherit', 'initial', 'unset', 'revert', 'revert-layer'] as const;
const NONE_UNIT_OR_KEYWORD = 'None';

@Component({
  selector: 'sfo-font-size-css-input',
  templateUrl: './font-size-css-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FontSizeCssInputComponent),
      multi: true,
    },
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltip,
  ],
})
/**
 * ControlValueAccessor that edits CSS font-size values as a number + unit or keyword.
 * Accepts units (pt, px, rem, %) and keywords (inherit, initial, unset, revert, revert-layer).
 */
export class FontSizeCssInputComponent implements ControlValueAccessor, OnInit {
  units = UNITS;
  keywords = KEYWORDS;
  noneUnitOrKeyword = NONE_UNIT_OR_KEYWORD;

  schema = input<SfoUiJSONSchema7>();
  aFormControl = input<FormControl | undefined>();

  // Get the value of the schema default as a number
  schemaDefaultValue = computed(() => {
    const raw = this.schema()?.default?.toString() ?? '';
    const match = LENGTH_REGEX.exec(raw);
    return match?.[1] ?? '';
  });

  // Get the unit or keyword of the schema default
  schemaDefaultUnitOrKeyword = computed(() => {
    const raw = this.schema()?.default?.toString() ?? '';
    const match = LENGTH_REGEX.exec(raw);
    return match?.[2] ?? '';
  });

  form = new FormGroup({
    amount: new FormControl<number | null>(null, [Validators.min(0)]),
    unitOrKeyword: new FormControl<string>('pt', { nonNullable: true }),
  });

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};
  private destroyRef = inject(DestroyRef);
  private isDisabled = false;

  ngOnInit(): void {
    this.syncKeywordState(this.form.controls.unitOrKeyword.value);

    this.form.controls.unitOrKeyword.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.syncKeywordState(value);
      });

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.emitValue();
    });
  }

  writeValue(value: string | null): void {
    // Check if it's a null value
    // If it is, set the form to the schema default.
    if (!value) {
      this.applyDefaultValue();
      return;
    }

    // Check if it's a keyword
    if (KEYWORDS.includes(value as (typeof KEYWORDS)[number])) {
      this.form.reset({ amount: null, unitOrKeyword: value }, { emitEvent: false });
      this.syncKeywordState(value);
      return;
    }

    // Handles length
    const match = LENGTH_REGEX.exec(value);
    if (match) {
      const amount = Number(match[1]);
      const unit = match[2];
      this.form.reset({ amount, unitOrKeyword: unit }, { emitEvent: false });
      this.syncKeywordState(unit);
      return;
    }

    this.applyDefaultValue();
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
      this.syncKeywordState(this.form.controls.unitOrKeyword.value);
    }
  }

  onBlur(): void {
    this.onTouched();
  }

  private emitValue(): void {
    this.validateUnitKeywordSelection();
    const { amount, unitOrKeyword } = this.form.getRawValue();

    if (unitOrKeyword === NONE_UNIT_OR_KEYWORD) {
      this.onChange(null);
      return;
    }

    if (KEYWORDS.includes(unitOrKeyword as (typeof KEYWORDS)[number])) {
      this.onChange(unitOrKeyword);
      return;
    }

    if (amount === null || amount === undefined || !unitOrKeyword) {
      this.onChange(null);
      return;
    }

    if (this.form.controls.amount.invalid) {
      this.onChange(null);
      return;
    }

    this.onChange(`${amount}${unitOrKeyword}`);
  }

  private syncKeywordState(value: string | null): void {
    if (this.isDisabled) {
      return;
    }

    if (value && KEYWORDS.includes(value as (typeof KEYWORDS)[number])) {
      this.form.controls.amount.setValue(null, { emitEvent: false });
      this.form.controls.amount.disable({ emitEvent: false });
      this.form.controls.unitOrKeyword.enable({ emitEvent: false });
    } else {
      this.form.controls.amount.enable({ emitEvent: false });
      this.form.controls.unitOrKeyword.enable({ emitEvent: false });
    }
  }

  private applyDefaultValue(): void {
    const rawDefault = this.schema()?.default?.toString() ?? '';
    if (KEYWORDS.includes(rawDefault as (typeof KEYWORDS)[number])) {
      this.form.reset({ amount: null, unitOrKeyword: rawDefault }, { emitEvent: false });
      this.syncKeywordState(rawDefault);
      return;
    }

    const defaultAmount = this.schemaDefaultValue();
    const defaultUnit = this.schemaDefaultUnitOrKeyword();
    if (defaultAmount && defaultUnit) {
      this.form.reset(
        { amount: Number(defaultAmount), unitOrKeyword: defaultUnit },
        { emitEvent: false },
      );
      this.syncKeywordState(defaultUnit);
      return;
    }

    this.form.reset({ amount: null, unitOrKeyword: 'pt' }, { emitEvent: false });
    this.syncKeywordState('pt');
  }

  private validateUnitKeywordSelection(): void {
    const amount = this.form.controls.amount.value;
    const unitOrKeyword = this.form.controls.unitOrKeyword.value;
    const hasAmount = amount !== null && amount !== undefined;
    const existingErrors = this.form.controls.unitOrKeyword.errors ?? {};

    if (unitOrKeyword === NONE_UNIT_OR_KEYWORD && hasAmount) {
      this.form.controls.unitOrKeyword.setErrors({ ...existingErrors, invalidSelection: true });
      return;
    }

    if (!existingErrors.invalidSelection) {
      return;
    }

    const { invalidSelection, ...remainingErrors } = existingErrors;
    void invalidSelection;
    this.form.controls.unitOrKeyword.setErrors(
      Object.keys(remainingErrors).length ? remainingErrors : null,
    );
  }
}
